/**
 * Skeleton — shimmer loading placeholders for content.
 *
 * Renders a shape matching the target content with a horizontal
 * shimmer gradient animation. Respects useReducedMotion().
 *
 * Includes compound helpers: Skeleton.Text, Skeleton.Card, Skeleton.Avatar.
 *
 * @see docs/milestones/M0.6-UI-PRIMITIVES.md §9
 */

import React, { useEffect } from 'react'
import { View, type DimensionValue } from 'react-native'
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withDelay,
  withRepeat,
  withTiming,
  useReducedMotion,
  Easing,
} from 'react-native-reanimated'
import { LinearGradient } from 'expo-linear-gradient'
import { useTheme } from '../../lib/hooks/use-theme'
import {
  useSemanticColors,
  withAlpha,
  DURATION,
  STAGGER,
  MAX_STAGGER_STEPS,
} from '../../lib/theme/tokens'
import { cn } from '../../lib/theme/variants'

// ─── Types ──────────────────────────────────────────────────────────────────

interface SkeletonProps {
  /** Width of the skeleton element */
  width?: DimensionValue
  /** Height of the skeleton element */
  height?: DimensionValue
  /** Border radius (circular for avatars) */
  borderRadius?: number
  /** Whether to show the shimmer animation (default: true) */
  animate?: boolean
  /** Additional NativeWind className */
  className?: string
  /**
   * 0-based index used to stagger the shimmer start by one STAGGER unit × index.
   * Pass it from the iteration index when rendering a list of placeholders
   * (e.g. `cards.map((_, i) => <Skeleton.Card staggerIndex={i} />)`) so
   * the shimmer waves move organically across the row instead of pulsing
   * in sync — sync looks like an old CRT, stagger looks like Linear/Stripe.
   */
  staggerIndex?: number
}

// ─── Shimmer Width (for gradient translation) ───────────────────────────────

const SHIMMER_WIDTH = 200

/**
 * Alpha of the travelling band in light mode, where it is a veil of the card
 * surface itself — so the sweep only ever changes the bar's lightness, never
 * its hue.
 *
 * `shimmerHighlight` is already a neutral white overlay in dark mode, but in
 * light mode it is an amber tint, which swept a warm beige band across
 * cool-grey bars. Two colour temperatures in one element reads as a rendering
 * artefact rather than as loading.
 */
const SHIMMER_VEIL_ALPHA = 0.55

// ─── Base Skeleton ──────────────────────────────────────────────────────────

function SkeletonBase({
  width = '100%',
  height = 16,
  borderRadius = 4,
  animate = true,
  className,
  staggerIndex = 0,
}: SkeletonProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const reducedMotion = useReducedMotion()
  const translateX = useSharedValue(-SHIMMER_WIDTH)
  const shimmerHighlight = isDark
    ? colors.shimmerHighlight
    : withAlpha(colors.surface, SHIMMER_VEIL_ALPHA)

  const shouldAnimate = animate && !reducedMotion

  useEffect(() => {
    if (!shouldAnimate) return
    // Stagger: each row enters the shimmer cycle one STAGGER unit later than
    // the row above it, capped so the last row of a long list never feels
    // broken / late.
    const staggerMs = Math.min(staggerIndex, MAX_STAGGER_STEPS) * STAGGER
    translateX.value = withDelay(
      staggerMs,
      withRepeat(
        withTiming(SHIMMER_WIDTH * 2, { duration: DURATION.verySlow, easing: Easing.linear }),
        -1,
        false,
      ),
    )
  }, [translateX, shouldAnimate, staggerIndex])

  const shimmerStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }))

  return (
    <View
      className={cn(className)}
      // Every placeholder in the app composes this one, so this is the single
      // signal that a screen is still loading. The screen audit waits for it to
      // clear: without it the harness settled on "the page has some text",
      // which the tab bar satisfies instantly, and measured the shimmer instead
      // of the vehicle detail page.
      testID="ui.skeleton"
      accessibilityElementsHidden={true}
      importantForAccessibility="no-hide-descendants"
      style={{
        width,
        height,
        borderRadius,
        backgroundColor: colors.shimmerBase,
        overflow: 'hidden',
      }}
    >
      {shouldAnimate && (
        <Animated.View
          style={[
            {
              position: 'absolute',
              top: 0,
              bottom: 0,
              width: SHIMMER_WIDTH,
            },
            shimmerStyle,
          ]}
        >
          <LinearGradient
            colors={[colors.shimmerBase, shimmerHighlight, colors.shimmerBase]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={{ flex: 1 }}
          />
        </Animated.View>
      )}
    </View>
  )
}

// ─── Skeleton.Text ──────────────────────────────────────────────────────────

interface SkeletonTextProps {
  /** Number of text lines */
  lines?: number
  /** Width of the last line */
  lastLineWidth?: DimensionValue
  /** Additional NativeWind className */
  className?: string
}

function SkeletonText({ lines = 3, lastLineWidth = '60%', className }: SkeletonTextProps) {
  return (
    <View className={cn('gap-2', className)}>
      {Array.from({ length: lines }, (_, i) => (
        <SkeletonBase
          key={i}
          height={14}
          borderRadius={4}
          width={i === lines - 1 ? lastLineWidth : '100%'}
        />
      ))}
    </View>
  )
}

// ─── Skeleton.Card ──────────────────────────────────────────────────────────

interface SkeletonCardProps {
  /** Height of the image area */
  imageHeight?: number
  /** Additional NativeWind className */
  className?: string
  /** Stagger offset — see SkeletonProps.staggerIndex. Pass list iteration index. */
  staggerIndex?: number
}

function SkeletonCard({ imageHeight = 200, className, staggerIndex }: SkeletonCardProps) {
  return (
    <View className={cn('rounded-xl overflow-hidden', className)}>
      <SkeletonBase height={imageHeight} borderRadius={0} staggerIndex={staggerIndex} />
      <View className="p-4 gap-3">
        <SkeletonBase height={18} width="75%" borderRadius={4} staggerIndex={staggerIndex} />
        <SkeletonText lines={2} lastLineWidth="50%" />
      </View>
    </View>
  )
}

// ─── Skeleton.Avatar ────────────────────────────────────────────────────────

const AVATAR_SIZES = { xs: 24, sm: 32, md: 40, lg: 56, xl: 80 } as const

interface SkeletonAvatarProps {
  size?: keyof typeof AVATAR_SIZES
  /** Additional NativeWind className */
  className?: string
}

function SkeletonAvatar({ size = 'md', className }: SkeletonAvatarProps) {
  const d = AVATAR_SIZES[size]
  return <SkeletonBase width={d} height={d} borderRadius={d / 2} className={className} />
}

// ─── Export compound component ──────────────────────────────────────────────

export const Skeleton = Object.assign(SkeletonBase, {
  Text: SkeletonText,
  Card: SkeletonCard,
  Avatar: SkeletonAvatar,
})
